<template>
  <div class="card">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h4 class="mb-0">{{ $t("avoir.title") }}</h4>
      <a-button type="primary" icon="plus" class="action-button" @click="openCreateModal">{{ $t("avoir.newCreditNote") }}</a-button>
    </div>
    <div class="card-body">
      <div class="table-container">
        <a-table
          row-key="_id"
          :loading="loading"
          :columns="columns"
          :data-source="rows"
          :pagination="tablePagination"
          @change="onTableChange"
          :scroll="{ x: 'max-content' }"
          :row-class-name="avoirRowClassName"
          :custom-row="(r) => ({ on: { click: () => openDetailsDrawer(r) } })"
        >
          <template slot="amount" slot-scope="text">
            <span class="amount-cell">{{ text != null ? text.toFixed(3) : "—" }}</span>
          </template>
          <template slot="status" slot-scope="text">
            <a-tag :color="statusColor(text)">{{ statusLabel(text) }}</a-tag>
          </template>
          <template slot="createdBy" slot-scope="text">
            <span class="cell-ellipsis">{{ formatCreatedBy(text) }}</span>
          </template>
          <template slot="createdAt" slot-scope="text">
            <span class="date-cell">{{ formatAvoirCreatedAt(text) }}</span>
          </template>
          <template slot="action" slot-scope="text, record">
            <div class="avoir-actions-row">
              <a-tooltip :title="$t('action.consulter')">
                <a-button
                  type="link"
                  size="small"
                  class="avoir-btn-consult"
                  @click.stop="openDetailsDrawer(record)"
                >
                  <a-icon type="eye" />
                </a-button>
              </a-tooltip>
              <a-button
                v-if="record.status === 'pending_reception'"
                type="primary"
                size="small"
                class="avoir-btn-confirm"
                @click.stop="openReceiveModal(record)"
              >{{ $t("avoir.confirmReception") }}</a-button>
            </div>
          </template>
        </a-table>
      </div>
      <div v-if="!loading && rows.length === 0" class="text-center text-muted py-4 empty-state">
        {{ $t("avoir.noCreditNotes") }}
      </div>
    </div>

    <a-modal
      :visible="createVisible"
      :title="$t('avoir.newCreditNote')"
      :confirm-loading="creating"
      :width="700"
      @ok="submitCreate"
      @cancel="createVisible = false"
      destroy-on-close
    >
      <a-form :form="createForm" layout="vertical">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('bulletin.label')">
              <a-input v-decorator="['label', { rules: [{ required: true, message: $t('requis.designation') }] }]" />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.reference')">
              <a-input v-decorator="['reference']" />
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('avoir.selectProvider')">
              <a-select
                v-decorator="['provider', { rules: [{ required: true, message: $t('requis.fournisseur') }] }]"
                show-search
                option-filter-prop="children"
                @change="onProviderChange"
              >
                <a-select-option v-for="p in providers" :key="p._id" :value="p._id">{{ p.fullName }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('avoir.selectInvoice')">
              <a-select
                v-decorator="['depense', { rules: [{ required: true, message: $t('requis.required') }] }]"
                show-search
                option-filter-prop="children"
              >
                <a-select-option v-for="inv in filteredInvoices" :key="inv._id" :value="inv._id">
                  {{ inv.invoiceNumber || inv.reference || inv.label }} - {{ (inv.amountTTC || inv.amount || inv.money || 0).toFixed(3) }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('recette.montant')">
              <a-input-number
                v-decorator="['amount', { rules: [{ required: true, message: $t('requis.required') }] }]"
                :min="0.001"
                :step="0.001"
                style="width: 100%"
              />
            </a-form-item>
          </div>
        </div>
        <a-form-item :label="$t('comptabilite.description')">
          <a-textarea v-decorator="['description']" :rows="2" />
        </a-form-item>
      </a-form>
    </a-modal>

    <avoir-receive-modal
      :visible="receiveVisible"
      :confirming="receiving"
      :total-amount="receiveTotalAmount"
      :lookups="lookups"
      @cancel="receiveVisible = false"
      @confirm="onReceiveConfirm"
    />

    <avoir-detail-drawer
      :visible="detailVisible"
      :avoir-id="detailAvoirId"
      @close="onDetailDrawerClose"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import {
  fetchCreditNotes,
  fetchOccupiedDepensesForAvoir,
  createCreditNote,
  receiveCreditNote,
} from '@/services/api/compV3/paymentEntry'
import { fetchDecaissementLookups } from '@/services/api/compV3/decaissement'
import { showApiError } from '@/utilities/showApiError'
import AvoirReceiveModal from './components/AvoirReceiveModal.vue'
import AvoirDetailDrawer from './components/AvoirDetailDrawer.vue'
import moment from 'moment'

export default {
  name: 'AvoirPage',
  components: { AvoirReceiveModal, AvoirDetailDrawer },
  beforeMount() {
    this.createForm = this.$form.createForm(this, { name: 'createAvoir' })
  },
  data() {
    return {
      rows: [],
      loading: false,
      pagination: { current: 1, pageSize: 20, total: 0 },
      providers: [],
      invoices: [],
      selectedProvider: null,
      createVisible: false,
      creating: false,
      receiveVisible: false,
      receiving: false,
      receivingId: null,
      receiveTotalAmount: 0,
      lookups: { myCaisse: null, otherCaisses: [], depenseCashCaisses: [], caisseBanks: [] },
      detailVisible: false,
      detailAvoirId: null,
      /** Depense ids that already have an active (non-cancelled) avoir — strings from API. */
      occupiedDepenseIds: [],
    }
  },
  computed: {
    filteredInvoices() {
      let list = !this.selectedProvider
        ? this.invoices
        : this.invoices.filter(
          (i) => i.provider && (typeof i.provider === 'object' ? i.provider._id : i.provider) === this.selectedProvider,
        )
      if (this.occupiedDepenseIds.length) {
        const occ = this.occupiedDepenseIds
        list = list.filter((inv) => !occ.includes(String(inv._id)))
      }
      return list.filter((inv) => this.isInvoiceFullyPaid(inv))
    },
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (t) => `${t}`,
      }
    },
    columns() {
      return [
        { title: this.$t('bulletin.label'), dataIndex: 'label', key: 'label', ellipsis: true },
        { title: this.$t('recette.montant'), dataIndex: 'amount', key: 'amount', scopedSlots: { customRender: 'amount' } },
        { title: this.$t('action.status'), dataIndex: 'status', key: 'status', scopedSlots: { customRender: 'status' } },
        { title: this.$t('library.createdBy'), dataIndex: 'createdBy', key: 'createdBy', scopedSlots: { customRender: 'createdBy' }, ellipsis: true },
        { title: this.$t('library.createdAt'), dataIndex: 'createdAt', key: 'createdAt', scopedSlots: { customRender: 'createdAt' } },
        { title: this.$t('action.actions'), key: 'action', scopedSlots: { customRender: 'action' }, align: 'center' },
      ]
    },
  },
  async created() {
    await this.loadAll()
  },
  methods: {
    isInvoiceFullyPaid(inv) {
      if (!inv) return false
      if (inv.invoiceStatus === 'paid') return true
      const total = Number(
        inv.amountTTC != null ? inv.amountTTC : inv.amount != null ? inv.amount : inv.money || 0,
      )
      const paid = Number(inv.totalPaid) || 0
      if (total <= 0) return false
      return paid + 0.009 >= total
    },
    avoirRowClassName(record, index) {
      return index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
    },
    statusColor(s) {
      if (s === 'received') return 'green'
      if (s === 'cancelled') return 'default'
      return 'orange'
    },
    statusLabel(s) {
      if (s === 'received') return this.$t('avoir.received')
      if (s === 'cancelled') return this.$t('avoir.cancelled')
      return this.$t('avoir.pendingReception')
    },
    formatCreatedBy(val) {
      if (val == null || val === '' || val === '--') return '—'
      return String(val)
    },
    formatAvoirCreatedAt(val) {
      if (!val) return '—'
      return moment(val).format('DD/MM/YYYY HH:mm')
    },
    async loadAll() {
      this.loading = true
      try {
        const [notesRes, occupiedRes, provRes, lookupsRes, depRes] = await Promise.all([
          fetchCreditNotes({ page: this.pagination.current, pageSize: this.pagination.pageSize }),
          fetchOccupiedDepensesForAvoir().catch(() => ({ data: { depenseIds: [] } })),
          apiClient.post('/providers/filter', { query: { status: 'active' } }),
          fetchDecaissementLookups({ forEncaissement: true }),
          apiClient.get('/compV3/decaissement/depenses'),
        ])
        const body = notesRes.data || {}
        this.occupiedDepenseIds = occupiedRes.data?.depenseIds || []
        this.rows = body.data || []
        this.pagination.total = body.total || 0
        this.pagination.current = body.page || 1
        this.providers = provRes.data || []
        this.invoices = depRes.data || []
        const ps = lookupsRes.data?.paymentSources || {}
        this.lookups = {
          myCaisses: ps.myCaisses || (ps.myCaisse ? [ps.myCaisse] : []),
          otherCaisses: ps.otherCaisses || [],
          depenseCashCaisses: ps.depenseCashCaisses || [],
          caisseBanks: ps.caisseBanks || [],
        }
      } catch { this.$message.error(this.$t('warning.connectionProblem')) } finally { this.loading = false }
    },
    onTableChange(pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.loadAll()
    },
    onProviderChange(val) { this.selectedProvider = val },
    async openCreateModal() {
      this.createVisible = true
      this.selectedProvider = null
      try {
        const res = await fetchOccupiedDepensesForAvoir()
        this.occupiedDepenseIds = res.data?.depenseIds || []
      } catch (_) {
        /* keep previous occupied list */
      }
    },
    submitCreate(e) {
      e.preventDefault()
      this.createForm.validateFields(async (err, values) => {
        if (err) return
        if (values.depense && this.occupiedDepenseIds.includes(String(values.depense))) {
          this.$message.error(this.$t('avoir.invoiceAlreadyHasAvoir'))
          return
        }
        const picked = this.invoices.find((i) => String(i._id) === String(values.depense))
        if (picked && !this.isInvoiceFullyPaid(picked)) {
          this.$message.error(this.$t('avoir.invoiceNotFullyPaid'))
          return
        }
        this.creating = true
        try {
          await createCreditNote(values)
          this.$message.success(this.$t('avoir.creditNoteCreated'))
          this.createVisible = false
          await this.loadAll()
        } catch (ex) {
          showApiError(this, ex)
        } finally { this.creating = false }
      })
    },
    openDetailsDrawer(record) {
      const id = record._id != null ? String(record._id) : ''
      if (!id) return
      this.detailAvoirId = id
      this.detailVisible = true
    },
    onDetailDrawerClose() {
      this.detailVisible = false
      this.detailAvoirId = null
    },
    openReceiveModal(record) {
      this.receivingId = record._id
      this.receiveTotalAmount = Number(record.amount) || 0
      this.receiveVisible = true
    },
    async onReceiveConfirm({ splits, receiptNumber }) {
      this.receiving = true
      try {
        await receiveCreditNote(this.receivingId, { splits, receiptNumber })
        this.$message.success(this.$t('avoir.receptionConfirmed'))
        this.receiveVisible = false
        await this.loadAll()
      } catch (ex) {
        showApiError(this, ex)
      } finally {
        this.receiving = false
      }
    },
  },
}
</script>

<style>
@import "~@/views/compV3/common.css";
.table-container .ant-table-tbody > tr {
  cursor: pointer;
}
.avoir-actions-row {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  white-space: nowrap;
  justify-content: center;
}
.avoir-btn-consult {
  padding: 0 4px;
  min-width: auto;
  line-height: 1;
}
.avoir-btn-confirm {
  flex-shrink: 0;
}
</style>
